import { AlertDiamond, CheckCircle, Cross } from '@ui/components/icon';
import { InfoSolid } from '@ui/components/v2/icon';
import { wrapClassNames } from '@ui/utils/tailwind';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import type { ExternalToast, ToastClassnames, ToasterProps } from 'sonner';
import { toast as sonnerToast } from 'sonner';

type Variants = 'success' | 'error' | 'warning';

export type VariantHeaders = {
  [variant in Variants]: string;
};

export { Toaster } from 'sonner';

type BasicVariants = Variants | 'default' | 'info';

const toastConfig: { headers: VariantHeaders | undefined; headerless: boolean } = {
  headers: undefined,
  headerless: false,
};

export const configureToasts = (config: { variantHeaders?: VariantHeaders; hideHeader?: boolean }) => {
  toastConfig.headers = config.variantHeaders;
  toastConfig.headerless = config.hideHeader ?? false;
};

/** `headerText` overrides the per-variant header for a single toast. */
export type SendToastOptions = ExternalToast & { headerText?: string };

const send = (variant: BasicVariants, message: ReactNode, options?: SendToastOptions) => {
  const { headerText, ...rest } = options ?? {};
  const fire = variant === 'default' ? sonnerToast : sonnerToast[variant];

  if (toastConfig.headerless) {
    return fire(message, rest);
  }

  const title =
    headerText ?? toastConfig.headers?.[variant as Variants] ?? `${variant[0].toUpperCase()}${variant.slice(1)}`;

  // The testid stays on the header text itself (not sonner's `[data-title]`
  // wrapper) so `getByTestId('ui-toast-header-*')` keeps matching an element
  // whose text is exactly the header, as the e2e locators expect.
  return fire(<span data-testid={`ui-toast-header-${variant}`}>{title}</span>, {
    description: message,
    ...rest,
  });
};

export const sendToast = Object.assign(
  (message: ReactNode, options?: SendToastOptions) => send('default', message, options),
  {
    success: (message: ReactNode, options?: SendToastOptions) => send('success', message, options),
    error: (message: ReactNode, options?: SendToastOptions) => send('error', message, options),
    warning: (message: ReactNode, options?: SendToastOptions) => send('warning', message, options),
    info: (message: ReactNode, options?: SendToastOptions) => send('info', message, options),

    custom: (jsx: Parameters<typeof sonnerToast.custom>[0], options?: SendToastOptions) =>
      sonnerToast.custom(jsx, {
        ...options,
        className: `p-0! bg-transparent! shadow-none! min-w-0! ${options?.className ?? ''}`,
      }),
    dismiss: sonnerToast.dismiss,
  }
);

const iconBox = 'rounded-xl h-5 w-6 shrink-0 items-center flex justify-center text-white';
const bodyText = 'text-sm leading-4 font-normal text-white max-w-[350px] sm:max-w-[500px] first-letter:uppercase';

const variantBackgrounds: Record<BasicVariants, string> = {
  success: 'bg-green-700',
  error: 'bg-status-danger-bg',
  warning: 'bg-yellow-600',
  info: 'bg-background-ui',
  default: 'bg-gray-800/50',
};

export const toastIcons: NonNullable<ToasterProps['icons']> = {
  success: <CheckCircle width={16} height={16} className="text-white" />,
  error: <AlertDiamond width={16} height={16} />,
  warning: <AlertDiamond width={16} height={16} className="opacity-40" />,
  info: <InfoSolid size={16} />,
  close: <Cross width={10} height={10} />,
};

export const toastClassNames = (hideHeader = false): ToastClassnames => ({
  toast:
    'rounded-2xl shadow-300 p-3 flex items-center gap-2 min-w-[350px] focus-visible:outline-hidden focus-visible:ring-2! focus-visible:ring-white/70!',
  ...variantBackgrounds,
  icon: iconBox,
  content: 'flex flex-col grow',
  title: hideHeader ? bodyText : 'text-sm leading-4 font-medium text-white mb-1.5',
  description: bodyText,
  // sonner renders the close button first; the house design puts it last.
  closeButton:
    'order-last rounded-full flex items-center justify-center text-white w-5 h-5 transition ease-in-out duration-200 focus:outline-hidden hover:outline-hidden focus:ring-3 hover:ring-3 focus:ring-offset-1 hover:ring-offset-1 focus:ring-gray-400 hover:ring-gray-400',
});

/** Card shell for `sendToast.custom` content that needs the house surface. */
export const ToastWrapper: FC<
  PropsWithChildren<{
    variant?: BasicVariants | 'dark';
  }>
> = ({ variant = 'default', children }) => {
  const backgrounds = { ...variantBackgrounds, dark: 'bg-icon text-white' };

  return (
    <aside
      className={wrapClassNames(`rounded-2xl shadow-300 *:not-first:ml-2 p-3 flex items-center relative min-w-[350px]
        ${backgrounds[variant]}`)}
    >
      {children}
    </aside>
  );
};
